import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import ProfilePicture from "../../components/profielPicture";
import ProfileImage from "../../components/ProfileImage";
import Friendship from "./Friendship";
import UpdateProfilePicture from "../../components/profielPicture/UpdateProfilePicture";
import { getProfileImageUrl } from "../../helpers/getGravatarUrl";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function ProfielPictureInfos({
  profile,
  updateProfilePicture,
  visitor,
  photos,
  othername,
  onUpdateProfilePictureOpen,
}) {
  const [show, setShow] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorImage, setEditorImage] = useState("");
  const [displayPicture, setDisplayPicture] = useState(null);
  const fileInputRef = useRef(null);
  const [profileImageError, setProfileImageError] = useState(false);
  const pRef = useRef(null);

  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [viewerZoom, setViewerZoom] = useState(MIN_ZOOM);
  const [viewerOffset, setViewerOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  // Reset image error when profile picture changes from parent/server
  useEffect(() => {
    setProfileImageError(false);
  }, [profile.picture]);

  // Keep local override in sync when parent profile picture updates to the same value
  useEffect(() => {
    if (
      displayPicture !== null &&
      profile.picture === displayPicture
    ) {
      setDisplayPicture(null);
    }
  }, [profile.picture, displayPicture]);

  // Notify parent component when modal state changes
  useEffect(() => {
    if (onUpdateProfilePictureOpen) {
      onUpdateProfilePictureOpen(showEditor);
    }
  }, [showEditor, onUpdateProfilePictureOpen]);

  const handleProfilePictureUpdate = (newPicture) => {
    const nextPicture = newPicture || "";
    if (typeof updateProfilePicture === "function") {
      updateProfilePicture(nextPicture);
    }
    setDisplayPicture(nextPicture);
    setShow(false);
  };

  // Close editor if image cleared from inside editor
  const handleSetEditorImage = (img) => {
    if (!img) setShowEditor(false);
    setEditorImage(img);
  };

  const pictureSrc =
    displayPicture !== null ? displayPicture : profile.picture;

  const profilePhotoUrl =
    profileImageError
      ? "/images/default_pic.png"
      : getProfileImageUrl(pictureSrc, profile.email, 800) ||
        getProfileImageUrl(pictureSrc, profile.email, 200) ||
        "/images/default_pic.png";

  const profileDisplayName =
    profile.name ||
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    "Profile";

  const openEditorWithCurrentPicture = () => {
    setEditorImage(
      getProfileImageUrl(pictureSrc, profile.email, 400) ||
        "/images/default_pic.png"
    );
    setShowEditor(true);
  };

  const closePhotoViewer = useCallback(() => {
    setShowPhotoViewer(false);
    setViewerZoom(MIN_ZOOM);
    setViewerOffset({ x: 0, y: 0 });
  }, []);

  const openPhotoViewer = () => {
    setViewerZoom(MIN_ZOOM);
    setViewerOffset({ x: 0, y: 0 });
    setShowPhotoViewer(true);
  };

  const zoomBy = useCallback((delta) => {
    setViewerZoom((prev) =>
      Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number((prev + delta).toFixed(2))))
    );
  }, []);

  const handleViewerWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
  };

  const handleViewerPointerDown = (e) => {
    if (viewerZoom <= MIN_ZOOM) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: viewerOffset.x,
      originY: viewerOffset.y,
    };
  };

  const handleViewerPointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag.dragging) return;
    setViewerOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const handleViewerPointerUp = () => {
    dragStateRef.current.dragging = false;
  };

  const handleViewerDoubleClick = () => {
    if (viewerZoom > MIN_ZOOM) {
      setViewerZoom(MIN_ZOOM);
      setViewerOffset({ x: 0, y: 0 });
    } else {
      setViewerZoom(2);
    }
  };

  useEffect(() => {
    if (viewerZoom <= MIN_ZOOM) {
      setViewerOffset({ x: 0, y: 0 });
    }
  }, [viewerZoom]);

  useEffect(() => {
    if (!showPhotoViewer) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closePhotoViewer();
      if (e.key === "+" || e.key === "=") zoomBy(ZOOM_STEP);
      if (e.key === "-" || e.key === "_") zoomBy(-ZOOM_STEP);
      if (e.key === "0") {
        setViewerZoom(MIN_ZOOM);
        setViewerOffset({ x: 0, y: 0 });
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showPhotoViewer, zoomBy, closePhotoViewer]);

  return (
    <div className="profile_img_wrap">
      {show && (
        <ProfilePicture
          setShow={setShow}
          pRef={pRef}
          photos={photos}
          onProfilePictureUpdate={handleProfilePictureUpdate}
        />
      )}
      {/* New: direct editor overlay for existing profile picture */}
      {showEditor && (
        <div className="blur">
          <UpdateProfilePicture
            setImage={handleSetEditorImage}
            image={editorImage}
            setShow={setShowEditor}
            setError={() => {}}
            pRef={pRef}
            onPictureChange={handleProfilePictureUpdate}
          />
        </div>
      )}
      <div className="profile_w_left">
        <div className="profile_w_img">
          <div
            className="profile_w_bg"
            ref={pRef}
            onClick={openPhotoViewer}
            role="button"
            tabIndex={0}
            title="View profile picture"
            aria-label="View profile picture"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openPhotoViewer();
              }
            }}
          >
            <img
              src={
                profileImageError
                  ? "/images/default_pic.png"
                  : getProfileImageUrl(pictureSrc, profile.email, 200)
              }
              alt={profileDisplayName}
              className="profile_w_img_tag"
              onError={() => setProfileImageError(true)}
              onLoad={() => setProfileImageError(false)}
            />
          </div>
          {!visitor && (
            <div className="profile_circle_wrap">
              <div
                className="profile_circle hover1"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current.click();
                }}
              >
                <i className="camera_filled_icon"></i>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setEditorImage(event.target.result);
                      setShowEditor(true);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
          )}
        </div>
        <div className="profile_w_col">
          <div className="profile_identity">
            <div className="profile_name">
              {profileDisplayName}
              <div className="othername">{othername && `(${othername})`}</div>
            </div>
            <div className="profile_friend_count">
              {profile?.friends && (
                <div className="profile_card_count friends_count">
                  {profile?.friends.length === 0
                    ? ""
                    : profile?.friends.length === 1
                    ? "1 Friend"
                    : `${profile?.friends.length} Friends`}
                </div>
              )}
            </div>
          </div>
          <div className="profile_friend_imgs">
            {profile?.friends &&
              profile.friends.slice(0, 6).map((friend, i) => (
                <Link
                  to={`/profile/${friend.username || friend._id}`}
                  key={friend._id || i}
                >
                  <ProfileImage
                    src={friend.picture}
                    email={friend.email}
                    alt={friend.name || `${friend.first_name || ""} ${friend.last_name || ""}`.trim() || "Friend"}
                    style={{
                      transform: `translateX(${-i * 7}px)`,
                      zIndex: `${i}`,
                      width: '42px',
                      height: '42px',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    placeholderText=""
                  />
                </Link>
              ))}
          </div>
        </div>
      </div>
      {visitor ? (
        <Friendship friendshipp={profile?.friendship} profileid={profile._id} />
      ) : (
        <div className="profile_w_right">
          <div
            className="gray_btn"
            onClick={openEditorWithCurrentPicture}
          >
            <i className="edit_icon"></i>
            <span>Edit profile</span>
          </div>
        </div>
      )}

      {showPhotoViewer &&
        createPortal(
          <div
            className="profile_photo_viewer_overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Profile photo viewer"
            onClick={closePhotoViewer}
          >
            <button
              type="button"
              className="profile_photo_viewer_close"
              onClick={closePhotoViewer}
              aria-label="Close photo viewer"
            >
              ✕
            </button>

            <div
              className="profile_photo_viewer_toolbar"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="profile_photo_viewer_zoom_btn"
                onClick={() => zoomBy(-ZOOM_STEP)}
                disabled={viewerZoom <= MIN_ZOOM}
                aria-label="Zoom out"
                title="Zoom out"
              >
                −
              </button>
              <span className="profile_photo_viewer_zoom_label">
                {Math.round(viewerZoom * 100)}%
              </span>
              <button
                type="button"
                className="profile_photo_viewer_zoom_btn"
                onClick={() => zoomBy(ZOOM_STEP)}
                disabled={viewerZoom >= MAX_ZOOM}
                aria-label="Zoom in"
                title="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                className="profile_photo_viewer_zoom_btn profile_photo_viewer_reset_btn"
                onClick={() => {
                  setViewerZoom(MIN_ZOOM);
                  setViewerOffset({ x: 0, y: 0 });
                }}
                disabled={viewerZoom <= MIN_ZOOM}
                aria-label="Reset zoom"
                title="Reset zoom"
              >
                Reset
              </button>
            </div>

            <div
              className={`profile_photo_viewer_stage ${
                viewerZoom > MIN_ZOOM ? "is_zoomed" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
              onWheel={handleViewerWheel}
              onPointerDown={handleViewerPointerDown}
              onPointerMove={handleViewerPointerMove}
              onPointerUp={handleViewerPointerUp}
              onPointerCancel={handleViewerPointerUp}
              onDoubleClick={handleViewerDoubleClick}
            >
              <img
                src={profilePhotoUrl}
                alt={profileDisplayName}
                className="profile_photo_viewer_img"
                draggable={false}
                style={{
                  transform: `translate(${viewerOffset.x}px, ${viewerOffset.y}px) scale(${viewerZoom})`,
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
